import React, { createContext, useEffect, useRef, useState } from "react";
import { dynamicHeaderGenerator } from "../utils/dynamicHeaderGenerator";
import { fetchDataFromAPI } from "../api/dataGridApiService";
import { APIResponseType } from "../types";
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridSelectionModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import type { DataGridColumnDefOverride } from "types/DataGridTypes";
import { useQueryClient, isCancelledError } from "@tanstack/react-query";
import { buildGridFetchQueryKey } from "../utils/gridFetchUtils";

const GRID_ROW_ID_FIELD = "__gridRowId";
const SYNTHETIC_HEADER_LABELS: Record<string, string> = {
  nurturing_automation_status: 'Status',
};

function sortModelsEqual(a: GridSortModel, b: GridSortModel): boolean {
  if (a.length !== b.length) return false;
  return a.every((item, index) => {
    const other = b[index];
    return item.field === other?.field && item.sort === other?.sort;
  });
}

function normalizeSortDirection(input: unknown): 'asc' | 'desc' | null {
  const value = String(input ?? '').trim().toLowerCase();
  if (value === 'asc' || value === 'ascending' || value === '1') return 'asc';
  if (value === 'desc' || value === 'descending' || value === '-1') return 'desc';
  return null;
}

function resolveServerDefaultSortModel(defaultSort: unknown): GridSortModel {
  if (!defaultSort) return [];

  if (Array.isArray(defaultSort) && defaultSort.length > 0) {
    const first = defaultSort[0];
    return resolveServerDefaultSortModel(first);
  }

  if (typeof defaultSort === 'object') {
    const sortObj = defaultSort as Record<string, unknown>;
    const field =
      String(
        sortObj.field ??
          sortObj.columnField ??
          sortObj.column ??
          sortObj.key ??
          '',
      ).trim();
    if (!field) return [];
    const direction =
      normalizeSortDirection(sortObj.sort ?? sortObj.order ?? sortObj.direction) ??
      'desc';
    return [{ field, sort: direction }];
  }

  if (typeof defaultSort !== 'string') return [];
  const raw = defaultSort.trim();
  if (!raw) return [];

  if (raw.startsWith('-') && raw.length > 1) {
    return [{ field: raw.slice(1), sort: 'desc' }];
  }
  if (raw.startsWith('+') && raw.length > 1) {
    return [{ field: raw.slice(1), sort: 'asc' }];
  }

  const colonParts = raw.split(':');
  if (colonParts.length === 2) {
    const field = colonParts[0].trim();
    const direction = normalizeSortDirection(colonParts[1]) ?? 'desc';
    return field ? [{ field, sort: direction }] : [];
  }

  const spaceParts = raw.split(/\s+/);
  if (spaceParts.length === 2) {
    const direction = normalizeSortDirection(spaceParts[1]);
    if (direction) {
      return [{ field: spaceParts[0], sort: direction }];
    }
  }

  return [{ field: raw, sort: 'desc' }];
}

function applyColumnDefOverrides(
  columns: GridColDef[],
  overrides?: Record<string, DataGridColumnDefOverride>,
): GridColDef[] {
  if (!overrides || Object.keys(overrides).length === 0) return columns;
  return columns.map(col => {
    const o = overrides[col.field];
    if (!o) return col;
    const merged = { ...col, ...o } as GridColDef;
    if (o.flex !== undefined && o.flex > 0) {
      delete (merged as { width?: number }).width;
    }
    return merged;
  });
}

// Define the context type
export type DataGridContextType = {
  data: any[];
  setData: React.Dispatch<React.SetStateAction<any[]>>;
  headers: GridColDef[];
  setHeaders: React.Dispatch<React.SetStateAction<GridColDef[]>>;
  backendHeaders: any[];
  setBackendHeaders: React.Dispatch<React.SetStateAction<any[]>>;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
  setTotalPages: React.Dispatch<React.SetStateAction<number>>;
  totalDocuments: number;
  setTotalDocuments: React.Dispatch<React.SetStateAction<number>>;
  cache: Record<string, any>;
  setCache: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  error: string;
  setError: React.Dispatch<React.SetStateAction<string>>;
  columnSortModel: GridSortModel;
  setColumnSortModel: React.Dispatch<React.SetStateAction<GridSortModel>>;
  /** Default server sort from DataGridTypes; used when the grid emits an empty sort model. */
  defaultSortModel: GridSortModel;
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: React.Dispatch<
    React.SetStateAction<GridColumnVisibilityModel>
  >;
  columnFilterModel: GridFilterModel;
  setColumnFilterModel: React.Dispatch<React.SetStateAction<GridFilterModel>>;
  rowSelectionModel: GridSelectionModel;
  setRowSelectionModel: React.Dispatch<
    React.SetStateAction<GridSelectionModel>
  >;
  filterableFields: string[];
  fetchData: (
    endpoint: string,
    params?: Record<string, any>,
    page?: number,
    pageSize?: number,
    filters?: GridFilterModel,
    reset?: boolean,
    sortModel?: GridSortModel
  ) => Promise<void>;
  manualUpdateData: (rowId: string, updates: Record<string, any>) => void;
  forceUpdate: number;
};

// Create the context with default values
export const DataGridContext = createContext<DataGridContextType>({
  data: [],
  setData: () => {},
  headers: [],
  setHeaders: () => {},
  backendHeaders: [],
  setBackendHeaders: () => {},
  currentPage: 1,
  setCurrentPage: () => {},
  totalDocuments: 0,
  setTotalDocuments: () => {},
  cache: {},
  setCache: () => {},
  loading: false,
  setLoading: () => {},
  error: "",
  setError: () => {},
  columnSortModel: [],
  setColumnSortModel: () => {},
  defaultSortModel: [],
  columnVisibilityModel: {},
  setColumnVisibilityModel: () => {},
  columnFilterModel: { items: [] },
  setColumnFilterModel: () => {},
  rowSelectionModel: [],
  setRowSelectionModel: () => {},
  filterableFields: [],
  fetchData: async () => {},
  totalPages: 0,
  setTotalPages: () => {},
  manualUpdateData: () => {},
  forceUpdate: 0,
});

// Define the provider component
const DataGridProvider: React.FC<{ 
  children: React.ReactNode; 
  renderCellMapping: any[]; 
  onCellClick: (params: any, clickedElement?: string, extraData?: any) => void; 
  sortModel: GridSortModel; 
  visibleColumns: any[]; 
  filterableFields: string[]; 
  externalManualUpdate?: (rowId: string, updates: Record<string, any>) => void;
  /** Initial filter model (e.g. offers: is_active, end_date). Sent in filters param. */
  initialFilterModel?: GridFilterModel;
  /** Per-field column sizing merged after dynamic headers (from DataGridTypes instance). */
  columnDefOverrides?: Record<string, DataGridColumnDefOverride>;
  onTotalDocumentsChange?: (totalDocuments: number) => void;
  /**
   * When set, only rows whose `_id` is in this list are shown (client-side filter
   * after fetch). Avoids long `template_ids` query strings on GET.
   */
  allowedRowIds?: string[];
}> = ({
  children, renderCellMapping, onCellClick, sortModel, visibleColumns, filterableFields, externalManualUpdate, initialFilterModel,
  columnDefOverrides,
  onTotalDocumentsChange,
  allowedRowIds,
}) => {

  const isMounted = useRef(true);

  // on unmount, mark it false
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  const [data, setData] = useState<any[]>([]);
  const [headers, setHeaders] = useState<GridColDef[]>([]);
  const [backendHeaders, setBackendHeaders] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cache, setCache] = useState<Record<string, any>>({});
  const [columnSortModel, setColumnSortModel] = useState<GridSortModel>(sortModel);
  const [serverIndicatorSortModel, setServerIndicatorSortModel] = useState<GridSortModel>([]);

  useEffect(() => {
    if (sortModel.length > 0) {
      setColumnSortModel((prev) =>
        prev.length === 0 ? sortModel : prev,
      );
      setServerIndicatorSortModel([]);
    }
  }, [sortModel]);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});
  const [columnFilterModel, setColumnFilterModel] = useState<GridFilterModel>(
    initialFilterModel && initialFilterModel.items?.length ? initialFilterModel : { items: [] }
  );
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridSelectionModel>([]);
  const [forceUpdate] = useState(0);
  const rowSequenceRef = useRef(0);
  const inflightFetchByKeyRef = useRef<Map<string, Promise<APIResponseType>>>(new Map());

  const queryClient = useQueryClient();
  const onTotalDocumentsChangeRef = useRef(onTotalDocumentsChange);
  const allowedRowIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    onTotalDocumentsChangeRef.current = onTotalDocumentsChange;
  }, [onTotalDocumentsChange]);

  useEffect(() => {
    allowedRowIdsRef.current = new Set(
      (allowedRowIds ?? [])
        .map(id => String(id).trim())
        .filter(Boolean),
    );
  }, [allowedRowIds]);

  // Function to manually update a specific row in the data
  const manualUpdateData = (rowId: string, updates: Record<string, any>) => {
    if (!isMounted.current) return;

    // If external manualUpdate function is provided, use it
    if (externalManualUpdate) {
      externalManualUpdate(rowId, updates);
      return;
    }

    // Update the internal data state
    setData(prevData => {
      const updatedData = [...prevData];
      const rowIndex = updatedData.findIndex(
        row => row._id === rowId || row.__gridRowId === rowId,
      );

      if (rowIndex >= 0) {
        updatedData[rowIndex] = {
          ...updatedData[rowIndex],
          ...updates,
          last_update: new Date().toISOString()
        };
      }

      return updatedData;
    });

    // Also update React Query cache to ensure DataGrid shows the changes
    const queries = queryClient.getQueriesData({
      queryKey: ['GET'],
      exact: false
    });

    queries.forEach(([queryKey, queryData]) => {
      if (queryData && typeof queryData === 'object' && 'body' in queryData) {
        const apiResponse = queryData as any;
        if (apiResponse.body && apiResponse.body.data && Array.isArray(apiResponse.body.data)) {
          const dataIndex = apiResponse.body.data.findIndex(
            (row: any) => row._id === rowId || row.__gridRowId === rowId,
          );
          if (dataIndex >= 0) {
            const updatedQueryData = {
              ...apiResponse,
              body: {
                ...apiResponse.body,
                data: apiResponse.body.data.map((row: any, index: number) => 
                  index === dataIndex 
                    ? { ...row, ...updates, last_update: new Date().toISOString() }
                    : row
                )
              }
            };
            queryClient.setQueryData(queryKey, updatedQueryData);
          }
        }
      }
    });

    // Avoid forcing DataGrid remounts here; remount resets scroll position.
  };

  // Function to set column headers dynamically
  const setColumnHeaders = (
    data: any[],
    headers: any[],
    isOpportunity: boolean
  ) => {
    try {
      
      // Find prospects field in backend headers
      const prospectsHeader = headers.find(h => h.db_field === 'prospects' || h.input_file_field === 'prospects');
      
      if (data.length > 0 && renderCellMapping.length) {
        const viewWidth = window.innerWidth - 200;
        const mappedHeaders: GridColDef[] =
          applyColumnDefOverrides(
            dynamicHeaderGenerator(
              headers,
              data,
              isOpportunity,
              onCellClick,
              viewWidth,
              renderCellMapping,
              visibleColumns,
              filterableFields,
            ) || [],
            columnDefOverrides,
          );
        setHeaders(mappedHeaders);
      }
    } catch (error) {
      console.error("Error setting column headers:", error);
    }
  };

  // Function to fetch data from the API using TanStack Query
  const attachGridRowIds = (rows: any[], reset: boolean) => {
    if (!Array.isArray(rows)) {
      return [];
    }

    if (reset) {
      rowSequenceRef.current = 0;
    }

    return rows.map((row: any) => {
      rowSequenceRef.current += 1;
      return {
        ...row,
        [GRID_ROW_ID_FIELD]: `${rowSequenceRef.current}`,
      };
    });
  };

  const fetchData = async (
    endpoint: string,
    params: Record<string, any> = {},
    page: number = 1,
    pageSize: number = 20,
    filters: GridFilterModel = { items: [] },
    reset = false,
    sortModel: GridSortModel = []
  ) => {
    if (!isMounted.current) return;
    setLoading(true);
    setError("");

    try {
      const sortModelForRequest =
        serverIndicatorSortModel.length > 0 &&
        sortModelsEqual(sortModel, serverIndicatorSortModel)
          ? []
          : sortModel;
      const queryKey = buildGridFetchQueryKey(endpoint, params, page, filters, sortModelForRequest);
      const inflightKey = JSON.stringify(queryKey);

      // Always go through fetchQuery so invalidateQueries / stale data triggers a
      // network refetch. A getQueryData shortcut would reuse invalidated cache.
      let responsePromise = inflightFetchByKeyRef.current.get(inflightKey);
      if (!responsePromise) {
        const createdPromise = queryClient.fetchQuery<APIResponseType>({
          queryKey,
          queryFn: () =>
            fetchDataFromAPI(
              endpoint,
              params,
              page,
              pageSize,
              JSON.stringify(filters),
              JSON.stringify(sortModelForRequest),
            ),
          // For first-page/reset fetches, force a fresh network hit so
          // parent remount patterns (`key={refreshKey}`) don't stay on stale cache.
          // Non-reset calls (e.g. scroll pagination) can still reuse short-lived cache.
          staleTime: reset ? 0 : 30_000,
        });
        createdPromise.catch(() => {});
        inflightFetchByKeyRef.current.set(inflightKey, createdPromise);
        void createdPromise.finally(() => {
          inflightFetchByKeyRef.current.delete(inflightKey);
        });
        responsePromise = createdPromise;
      }

      const response = await responsePromise;

      if (!response?.body) {
        return;
      }

      const serverDefaultSortModel = resolveServerDefaultSortModel(
        response.body.default_sort,
      );
      if (sortModel.length === 0 && serverDefaultSortModel.length > 0) {
        setColumnSortModel(serverDefaultSortModel);
        setServerIndicatorSortModel(serverDefaultSortModel);
      }

      const allowedSet = allowedRowIdsRef.current;
      const usesClientRowFilter = allowedSet.size > 0;

      // Update state with the fetched data
      if (!usesClientRowFilter) {
        const nextTotalDocuments = response.body.meta.totalDocuments;
        setTotalDocuments(nextTotalDocuments);
        onTotalDocumentsChangeRef.current?.(nextTotalDocuments);
      }
      setCurrentPage(page);
      setTotalPages(usesClientRowFilter ? 1 : response.body.meta.totalPages);
      
      // Store backend headers for filter panel
      setBackendHeaders(response.body.headers);

      // Merge synthetic headers for visibleColumns not returned by API (e.g. action column)
      const apiHeaders = response.body.headers || [];
      const apiFieldSet = new Set(apiHeaders.map((h: any) => h.db_field || h.input_file_field));
      const syntheticHeaders = (visibleColumns || []).filter(
        (field: string) => !apiFieldSet.has(field)
      ).map((field: string) => ({
        db_field: field,
        display_name:
          SYNTHETIC_HEADER_LABELS[field] ??
          field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' '),
        field_data_type: 'string',
        field_required: false,
        input_file_field: field,
        null_allowed: true,
      }));
      const mergedHeaders = [...apiHeaders, ...syntheticHeaders];

      if (headers.length === 0) {
        setColumnHeaders(
          response.body.data,
          mergedHeaders,
          response.body.meta.isOpportunity
        );
      }
      
      let nextRows = attachGridRowIds(response.body.data, reset);
      if (usesClientRowFilter) {
        nextRows = nextRows.filter(row => {
          const rowId = row?._id != null ? String(row._id).trim() : '';
          return rowId && allowedSet.has(rowId);
        });
      }

      if (reset) {
        setData(nextRows);
        if (usesClientRowFilter) {
          setTotalDocuments(nextRows.length);
          onTotalDocumentsChangeRef.current?.(nextRows.length);
        }
      } else {
        setData(old => {
          const merged = old.concat(nextRows);
          if (usesClientRowFilter) {
            setTotalDocuments(merged.length);
            onTotalDocumentsChangeRef.current?.(merged.length);
          }
          return merged;
        });
      }
    } catch (error) {
      if (!isMounted.current) return;
      if (isCancelledError(error)) return;
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("An unknown error occurred");
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  // Provide the context value
  const contextValue: DataGridContextType = {
    data,
    setData,
    headers,
    setHeaders,
    backendHeaders,
    setBackendHeaders,
    currentPage,
    setCurrentPage,
    totalPages,
    setTotalPages,
    totalDocuments,
    setTotalDocuments,
    cache,
    setCache,
    loading,
    setLoading,
    error,
    setError,
    columnSortModel,
    setColumnSortModel,
    defaultSortModel: sortModel,
    columnVisibilityModel,
    setColumnVisibilityModel,
    columnFilterModel,
    setColumnFilterModel,
    rowSelectionModel,
    setRowSelectionModel,
    filterableFields,
    fetchData,
    manualUpdateData: manualUpdateData,
    forceUpdate: forceUpdate,
  };

  return (
    <DataGridContext.Provider value={contextValue}>
      {children}
    </DataGridContext.Provider>
  );
};

export default DataGridProvider;